import { motion } from "framer-motion";
import { ArrowRight, Check, Monitor } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { PhoneMockup } from "@/components/PhoneMockup";
import { AppStoreButtons } from "@/components/AppStoreButtons";
import { Contours } from "@/components/Contours";
import { SCREENS, WEB_APP_URL, WEB_APP_ENABLED } from "@/data/content";

const fade = (delay = 0) => ({
  initial: { opacity: 0, y: 22 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.7, delay, ease: [0.2, 0.7, 0.2, 1] },
});

export const Hero = () => {
  const { t } = useLanguage();

  return (
    <section id="top" className="relative overflow-hidden">
      {/* backdrop */}
      <div className="absolute inset-0" aria-hidden="true">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_70%_20%,rgba(127,121,57,0.22),transparent_55%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_10%_80%,rgba(15,55,43,0.35),transparent_55%)]" />
        <Contours rings={16} step={52} seed={11} cx={1000} cy={380} opacity={0.14} />
        <div className="absolute inset-x-0 bottom-0 h-64 bg-gradient-to-b from-transparent to-[var(--bg)]" />
      </div>

      <div className="relative mx-auto grid max-w-7xl items-center gap-14 px-5 pb-20 pt-32 sm:px-6 md:px-8 lg:grid-cols-12 lg:gap-6 lg:pb-28 lg:pt-40">
        <div className="lg:col-span-6">
          <motion.p {...fade(0)} className="eyebrow" data-testid="hero-eyebrow">
            {t.hero.eyebrow}
          </motion.p>

          <motion.h1
            {...fade(0.08)}
            className="font-display mt-6 text-[2.7rem] leading-[1.02] text-[var(--ink)] sm:text-6xl lg:text-[4.3rem] xl:text-[4.9rem]"
          >
            {t.hero.titleA}
            <br />
            <span className="text-[var(--sage)]">{t.hero.titleB}</span>
          </motion.h1>

          <motion.p {...fade(0.16)} className="mt-7 max-w-xl text-base leading-relaxed text-[var(--ink-2)] md:text-lg">
            {t.hero.subtitle}
          </motion.p>

          <motion.div {...fade(0.24)} className="mt-9 flex flex-col gap-3 sm:flex-row sm:items-center">
            <a href="#preise" data-testid="hero-primary-cta" className="btn btn-primary px-7 py-3.5 text-base">
              {t.hero.primaryCta}
              <ArrowRight className="h-4.5 w-4.5" />
            </a>
            <a href="#revier" data-testid="hero-secondary-cta" className="btn btn-ghost px-7 py-3.5 text-base">
              {t.hero.secondaryCta}
            </a>
          </motion.div>

          <motion.ul {...fade(0.32)} className="mt-6 flex flex-wrap gap-x-5 gap-y-2 text-sm text-[var(--ink-2)]">
            {t.hero.trust.map((item) => (
              <li key={item} className="inline-flex items-center gap-2">
                <Check className="h-3.5 w-3.5 text-[var(--sage)]" />
                {item}
              </li>
            ))}
          </motion.ul>

          <motion.div {...fade(0.4)} className="mt-10">
            <AppStoreButtons idPrefix="hero" />
            {WEB_APP_ENABLED && (
              <p className="mt-4 flex items-center gap-2 text-sm text-[var(--ink-2)]">
                <Monitor className="h-4 w-4 text-[var(--sage)]" />
                {t.hero.webNote}{" "}
                <a
                  href={WEB_APP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="hero-web-app-link"
                  className="font-medium text-[var(--ink)] underline decoration-[var(--sage)]/60 underline-offset-4 transition-colors hover:text-white"
                >
                  {t.hero.webCta}
                </a>
              </p>
            )}
          </motion.div>
        </div>

        {/* devices */}
        <div className="relative mx-auto w-full max-w-[520px] lg:col-span-6 lg:max-w-none">
          <div className="relative mx-auto h-[520px] w-full sm:h-[640px] lg:h-[720px]">
            <div className="absolute left-1/2 top-6 w-[205px] -translate-x-[12%] sm:w-[260px] sm:-translate-x-[6%] lg:w-[290px] lg:-translate-x-[4%]">
              <motion.div
                initial={{ opacity: 0, y: 60, rotate: 10 }}
                animate={{ opacity: 1, y: 0, rotate: 7 }}
                transition={{ duration: 1, delay: 0.35, ease: [0.2, 0.7, 0.2, 1] }}
              >
                <PhoneMockup
                  src={SCREENS.revierKarte}
                  alt={t.hero.screenLabelBack}
                  loading="eager"
                  testId="hero-phone-back"
                  className="opacity-90 saturate-[0.9]"
                />
              </motion.div>
            </div>
            <div className="absolute left-1/2 top-16 w-[225px] -translate-x-[84%] sm:w-[280px] sm:-translate-x-[90%] lg:w-[310px] lg:-translate-x-[92%]">
              <motion.div
                initial={{ opacity: 0, y: 60, rotate: -8 }}
                animate={{ opacity: 1, y: 0, rotate: -5 }}
                transition={{ duration: 1, delay: 0.2, ease: [0.2, 0.7, 0.2, 1] }}
              >
                <PhoneMockup src={SCREENS.dashboard} alt={t.hero.screenLabelFront} loading="eager" testId="hero-phone-front" />
              </motion.div>
            </div>
            {/* ground glow */}
            <div className="absolute inset-x-10 bottom-0 h-24 rounded-full bg-[var(--olive)]/25 blur-3xl" aria-hidden="true" />
          </div>
        </div>
      </div>
    </section>
  );
};
